import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addServicePosition,
    ServicePosition,
    defaultServicePosition,
    updateServicePosition,
    useServicePositionList,
} from 'api/servicePosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { getMwstCode, Kunde } from 'api/kunde';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { ArtikelDetailSelect } from 'components/Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface ServicePositionEditProps {
    kunde: Kunde;
    sNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const ServicePositionEdit: React.FC<ServicePositionEditProps> = ({
    kunde,
    sNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const servicePositionList = useServicePositionList(
        {
            sNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [servicePosition, setServicePosition] = React.useState<ServicePosition | null>(null);

    React.useEffect(() => {
        if (!servicePositionList.isLoading && !servicePositionList.isFetching && servicePosition === null) {
            if (servicePositionList.data?.length === 1) {
                setServicePosition(cloneDeep(servicePositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (servicePositionList.data[0].artikelGruppeNr && servicePositionList.data[0].artikelNr) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: servicePositionList.data[0].artikelGruppeNr,
                        artikelNr: servicePositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setServicePosition({ ...defaultServicePosition, sNr, pos, posMwst: getMwstCode(kunde) });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [servicePosition, servicePositionList, sNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !servicePositionList.isLoading &&
            !servicePositionList.isFetching &&
            servicePositionList.data?.length === 1 &&
            servicePosition !== null &&
            !isEqual(servicePositionList.data[0], servicePosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('servicePosition', servicePosition, servicePositionList.data[0]);
        }
    }, [savingState, servicePositionList, servicePosition]);

    React.useEffect(() => {
        if (servicePosition) {
            if (servicePosition.posMenge && servicePosition.posEPreis) {
                const netto = Math.round(servicePosition.posMenge * servicePosition.posEPreis * 100) / 100;
                const mwstFactor = servicePosition.posMwst ? 1 + servicePosition?.posMwst / 100 : 1;
                const brutto = servicePosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setServicePosition({
                    ...servicePosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setServicePosition({
                    ...servicePosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicePosition?.posMenge, servicePosition?.posEPreis, servicePosition?.posMwst]);

    const validate = (): boolean => {
        if (!servicePosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (servicePosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (servicePosition.pos === -1) {
                    addServicePosition(servicePosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateServicePosition(servicePosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof ServicePosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(servicePosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (servicePosition) {
                    setServicePosition({
                        ...servicePosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof ServicePosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(servicePosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (servicePosition) {
                    setServicePosition({
                        ...servicePosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof ServicePosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={servicePosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (servicePosition) {
                    setServicePosition({
                        ...servicePosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={servicePosition?.artikelGruppeNr}
            artikelNr={servicePosition?.artikelNr}
            disabled={servicePosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (servicePosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setServicePosition({
                            ...servicePosition,
                            artikelGruppeNr: defaultServicePosition.artikelGruppeNr,
                            artikelNr: defaultServicePosition.artikelNr,
                            posText: defaultServicePosition.posText,
                            posEkPreis: defaultServicePosition.posEkPreis,
                            posEPreis: defaultServicePosition.posEPreis,
                            posEinh: defaultServicePosition.posEinh,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const ekPreis = defaultKalkulation?.ek2 || 0;
                    const ePreis = defaultKalkulation?.vk || 0;

                    setServicePosition({
                        ...servicePosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof ServicePosition,
        ekPreisColumn: keyof ServicePosition,
        ePreisColumn: keyof ServicePosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(servicePosition?.[column] as string) || ''}
            menuPlacement="top"
            allowEmpty
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek2 || 0;
                    ePreis = kalkulation?.vk || 0;
                }

                if (servicePosition) {
                    setServicePosition({
                        ...servicePosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    const renderCheckbox = (column: keyof ServicePosition, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={servicePosition?.[column] as boolean}
            onChange={(event, checked) => {
                if (servicePosition) {
                    setServicePosition({
                        ...servicePosition,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title="Servicesposition"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1100}
            onDismiss={() => {
                onClose(false);
            }}
        >
            <form className={formStyles.form}>
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div>{renderArtikelDetailSelect()}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderCheckbox('posDoku', 'Doku-Druck')}</div>
                </div>
                <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                <div className={styles.posLine}>
                    <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                    <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posNetto', 'VK Netto', { disabled: true })}</div>
                    <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                    <div>{renderDecimalField('posBrutto', 'VK Brutto', { disabled: true })}</div>
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={styles.varianteHeader}>Variante 1</div>
                    <div className={styles.varianteHeader}>Variante 2</div>
                    <div className={styles.varianteHeader}>Variante 3</div>
                </div>

                <Timestamps
                    modifiedDate={servicePosition?.updDat}
                    modifiedDecimalTime={servicePosition?.updUhr}
                    modifiedUser={servicePosition?.updUser}
                    showCreated={false}
                />

                {validationError && <div className={formStyles.error}>{validationError}</div>}
                {savingState === SavingState.error && <div className={formStyles.error}>Fehler beim Speichern.</div>}

                <DialogFooter>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={savingState === SavingState.notChanged || servicePositionList.isLoading}
                        />
                    )}
                </DialogFooter>
            </form>
            {(servicePositionList.isLoading || servicePositionList.isFetching || !servicePosition) && (
                <LoadingIndicator />
            )}
        </Dialog>
    );
};
